import React, { useState, useRef, useContext, useEffect } from "react";
import { CsvImportModalStyle } from "./CsvImportModal.style";
import Spinner from "../Spinner/Spinner";
import { LoginContext } from "../../context/LoginContext";
import { parseCsvFile, downloadSampleCsv, validateCsvFile, type PlaylistData } from "../../utils/csvParser";
import csvImportService, { type ImportProgress } from "../../services/csvImportService";
import { useCsvImportContext } from "../../context/CsvImportContext";
import { toast } from "react-toastify";

interface CsvImportModalProps {
  isModalVisible?: boolean;
  setIsModalVisible?: (_: boolean) => void;
}

enum ImportState {
  FILE_SELECTION = 'file_selection',
  PARSING = 'parsing',
  DATA_REVIEW = 'data_review',
  IMPORTING = 'importing',
  COMPLETED = 'completed'
}

const CsvImportModal = (props: CsvImportModalProps) => {
  const { isModalVisible, setIsModalVisible } = props;
  const { state: loginState } = useContext(LoginContext);
  const csvImportContext = useCsvImportContext();
  
  // File selection state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Import state
  const [importState, setImportState] = useState<ImportState>(ImportState.FILE_SELECTION);
  const [parsedData, setParsedData] = useState<PlaylistData[]>([]);
  const [importProgress, setImportProgress] = useState<ImportProgress>({
    completed: 0,
    total: 0,
    successful: [],
    failed: []
  });

  // Sync with global context when modal reopens
  useEffect(() => {
    if (isModalVisible && csvImportContext.isImporting && csvImportContext.progress) {
      setImportState(ImportState.IMPORTING);
      setImportProgress(csvImportContext.progress);
    }
  }, [isModalVisible, csvImportContext.isImporting, csvImportContext.progress]);

  const resetModal = () => {
    setSelectedFile(null);
    setError("");
    setImportState(ImportState.FILE_SELECTION);
    setParsedData([]);
    setImportProgress({
      completed: 0,
      total: 0,
      successful: [],
      failed: []
    });
  };

  const handleCancel = (e: any) => {
    e.stopPropagation();

    // If importing, minimize the modal instead of closing
    if (importState === ImportState.IMPORTING) {
      csvImportContext.minimizeModal();
      setIsModalVisible?.(false);
      return;
    }

    // If in data review state, go back to file selection instead of closing modal
    if (importState === ImportState.DATA_REVIEW) {
      setImportState(ImportState.FILE_SELECTION);
      return;
    }

    setIsModalVisible?.(false);
    resetModal();
  };

  const handleFileSelect = async (file: File) => {
    setError("");

    // Validate file before proceeding
    const validation = validateCsvFile(file);
    if (!validation.valid) {
      setError(validation.error || "Invalid file");
      return;
    }

    setSelectedFile(file);

    // Automatically parse the file after selection
    try {
      setImportState(ImportState.PARSING);
      setError("");

      const parseResult = await parseCsvFile(file);

      if (!parseResult.success) {
        setError(parseResult.error || "Failed to parse CSV file");
        setImportState(ImportState.FILE_SELECTION);
        return;
      }

      if (!parseResult.data || parseResult.data.length === 0) {
        setError("No valid playlist data found in CSV file");
        setImportState(ImportState.FILE_SELECTION);
        return;
      }

      setParsedData(parseResult.data);

      // Validate authentication before showing data review
      if (!loginState.token || !loginState.refreshToken || !loginState.userId) {
        setError("Please log in to Spotify to import playlists");
        setImportState(ImportState.FILE_SELECTION);
        return;
      }

      // Show parsed data for review
      setImportState(ImportState.DATA_REVIEW);
      setImportProgress({
        completed: 0,
        total: parseResult.data.length,
        successful: [],
        failed: []
      });

    } catch (error: any) {
      console.error("File parsing error:", error);
      setError(`Failed to parse CSV file: ${error.message || "An unexpected error occurred"}`);
      setImportState(ImportState.FILE_SELECTION);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError("Please select a CSV file to import");
      return;
    }

    // Validate authentication
    if (!loginState.token || !loginState.refreshToken || !loginState.userId) {
      setError("Please log in to Spotify to import playlists");
      return;
    }

    // If we don't have parsed data, trigger file selection parsing
    if (!parsedData || parsedData.length === 0) {
      await handleFileSelect(selectedFile);
      return;
    }

    // Validate authentication
    const authValidation = await csvImportService.validateAuthentication(loginState.token);
    if (!authValidation.valid) {
      setError(authValidation.error || "Authentication failed");
      setImportState(ImportState.FILE_SELECTION);
      return;
    }

    // Show parsed data for review
    setImportState(ImportState.DATA_REVIEW);
    setImportProgress({
      completed: 0,
      total: parsedData.length,
      successful: [],
      failed: []
    });
  };

  const startImportProcess = async () => {
    if (!parsedData || parsedData.length === 0) {
      setError("No playlist data available to import");
      return;
    }

    try {
      // Step 4: Start import process
      setImportState(ImportState.IMPORTING);
      csvImportContext.startImport();
      
      const initialProgress = {
        completed: 0,
        total: parsedData.length,
        successful: [],
        failed: []
      };
      setImportProgress(initialProgress);
      csvImportContext.updateProgress(initialProgress);

      const importResult = await csvImportService.importPlaylists(
        parsedData,
        loginState.token,
        loginState.refreshToken,
        loginState.userId,
        {
          onProgress: (progress) => {
            setImportProgress({ ...progress });
            csvImportContext.updateProgress({ ...progress });
          },
          onPlaylistCreated: (playlistName, spotifyId) => {
            console.log(`Created playlist: ${playlistName} (${spotifyId})`);
          },
          onError: (playlistName, error) => {
            console.error(`Failed to create playlist ${playlistName}: ${error}`);
          },
          retryAttempts: 2,
          delayBetweenRequests: 500
        }
      );

      // Step 5: Show results
      setImportState(ImportState.COMPLETED);
      csvImportContext.completeImport();

      if (importResult.success) {
        toast.success(importResult.message);

        // Close modal after successful import (playlists will refresh via context callback)
        setTimeout(() => {
          setIsModalVisible?.(false);
          resetModal();
        }, 3000);
      } else {
        if (importResult.cancelled) {
          toast.info("Import was cancelled");
          setImportState(ImportState.FILE_SELECTION);
          csvImportContext.cancelImport();
        } else {
          toast.error(importResult.message);
        }
      }

    } catch (error: any) {
      console.error("Import error:", error);
      setError(`Import failed: ${error.message || "An unexpected error occurred"}`);
      setImportState(ImportState.FILE_SELECTION);
      csvImportContext.cancelImport();
    }
  };

  const exampleCsvContent = `name,description
"My Chill Playlist","Relaxing songs for evening"
"Workout Mix","High energy songs for gym"
"Study Music","Instrumental focus music"`;

  const getProgressPercentage = () => {
    if (importProgress.total === 0) return 0;
    return Math.round((importProgress.completed / importProgress.total) * 100);
  };

  const getStateTitle = () => {
    switch (importState) {
      case ImportState.PARSING:
        return "Parsing CSV File...";
      case ImportState.DATA_REVIEW:
        return "Review Playlist Data";
      case ImportState.IMPORTING:
        return "Creating Playlists...";
      case ImportState.COMPLETED:
        return "Import Complete";
      default:
        return "Import CSV Playlists";
    }
  };

  const getStateDescription = () => {
    switch (importState) {
      case ImportState.PARSING:
        return "Validating your CSV file and playlist data...";
      case ImportState.DATA_REVIEW:
        return `Found ${parsedData.length} playlist(s) ready to import. Please review the details below.`;
      case ImportState.IMPORTING:
        return importProgress.currentPlaylist
          ? `Creating "${importProgress.currentPlaylist}"`
          : "Preparing to create playlists...";
      case ImportState.COMPLETED:
        return `Created ${importProgress.successful.length} playlist(s)${
          importProgress.failed.length > 0
            ? `, ${importProgress.failed.length} failed`
            : ""
        }`;
      default:
        return "";
    }
  };

  const renderFileSelection = () => (
    <>
      <div
        className={`upload-area ${isDragOver ? 'dragover' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="upload-icon">📁</div>
        <div className="upload-text">
          {selectedFile ? "Change file" : "Drag and drop your CSV file here"}
        </div>
        <div className="upload-subtext">
          or click to select a file (Max 5MB)
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleInputChange}
          className="file-input"
        />
      </div>

      {selectedFile && (
        <div className="file-info">
          <div className="file-details">
            <span className="file-icon">📄</span>
            <div>
              <div className="file-name">{selectedFile.name}</div>
              <div className="file-size">{formatFileSize(selectedFile.size)}</div>
            </div>
          </div>
          <button
            className="remove-file"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedFile(null);
              setError("");
            }}
          >
            ✕
          </button>
        </div>
      )}

      <div className="example-section">
        <div className="example-title">
          Example CSV Format:
          <button 
            className="download-sample"
            onClick={(e) => {
              e.stopPropagation();
              downloadSampleCsv();
            }}
            style={{ color: '#fff' }}
          >
            Download Sample
          </button>
        </div>
        <div className="example-csv">{exampleCsvContent}</div>
      </div>
    </>
  );

  const renderProgress = () => (
    <div className="progress-section">
      <div className="progress-info">
        <div className="progress-text">{getStateDescription()}</div>
        <div className="progress-stats">
          {importState === ImportState.IMPORTING && (
            <span>{importProgress.completed} of {importProgress.total} completed</span>
          )}
        </div>
      </div>

      {importState === ImportState.IMPORTING && (
        <>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
            <div className="progress-percentage">{getProgressPercentage()}%</div>
          </div>
          <div className="import-info" style={{ marginTop: '16px' }}>
            <div className="info-icon">💡</div>
            <div className="info-text">
              You can minimize this window and continue using the platform. 
              The import will continue in the background, and you can track progress from the CSV icon in the sidebar.
            </div>
          </div>
        </>
      )}

      {importState === ImportState.PARSING && (
        <div className="parsing-spinner">
          <Spinner />
        </div>
      )}
    </div>
  );

  const renderDataReview = () => (
    <div className="data-review-section">
      <div className="data-summary">
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-number">{parsedData.length}</span>
            <span className="stat-label">Total Playlists</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {parsedData.filter(p => p.description && p.description.trim() !== '').length}
            </span>
            <span className="stat-label">With Description</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {parsedData.filter(p => !p.description || p.description.trim() === '').length}
            </span>
            <span className="stat-label">Without Description</span>
          </div>
        </div>
      </div>

      <div className="playlist-preview">
        <h4>📋 Playlist Preview (First 10)</h4>
        <div className="playlist-list">
          {parsedData.slice(0, 10).map((playlist, index) => (
            <div key={index} className="playlist-item">
              <div className="playlist-info">
                <div className="playlist-name">
                  <span style={{ color: '#007AFF', marginRight: '8px' }}>#{index + 1}</span>
                  "{playlist.name}"
                </div>
                <div className="playlist-description">
                  {playlist.description ? `"${playlist.description}"` : '📝 No description'}
                </div>
              </div>
            </div>
          ))}
          {parsedData.length > 10 && (
            <div className="more-playlists">
              🎵 ... and {parsedData.length - 10} more playlists
            </div>
          )}
        </div>
      </div>

      {/* <div className="import-info">
        <div className="info-icon">ℹ️</div>
        <div className="info-text">
          <strong>Note:</strong> All playlists will be created as <strong>public</strong> on Spotify.
          You can change their visibility later in the Spotify app if needed.
        </div>
      </div> */}
    </div>
  );

  const renderResults = () => (
    <div className="results-section">
      <div className="results-summary">
        <div className="results-header">
          <span className="success-icon">✅</span>
          <h3>Import Summary</h3>
        </div>
        
        <div className="results-stats">
          <div className="stat-item success">
            <span className="stat-number">{importProgress.successful.length}</span>
            <span className="stat-label">Successful</span>
          </div>
          {importProgress.failed.length > 0 && (
            <div className="stat-item failed">
              <span className="stat-number">{importProgress.failed.length}</span>
              <span className="stat-label">Failed</span>
            </div>
          )}
        </div>

        {importProgress.failed.length > 0 && (
          <div className="failed-playlists">
            <h4>Failed Playlists:</h4>
            <div className="failed-list">
              {importProgress.failed.map((failure, index) => (
                <div key={index} className="failed-item">
                  <span className="playlist-name">"{failure.name}"</span>
                  <span className="error-reason">- {failure.error}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <CsvImportModalStyle
      visible={isModalVisible}
      onCancel={handleCancel}
      footer={null}
    >
      <div className="csvImportHead">
        <h1 className="heading">{getStateTitle()}</h1>
        <p className="description">
          Create multiple playlists at once by uploading a CSV file with titles and descriptions.
        </p>
      </div>
      
      <div className="csvImportForm">
        {importState === ImportState.FILE_SELECTION && renderFileSelection()}
        {importState === ImportState.DATA_REVIEW && renderDataReview()}
        {(importState === ImportState.PARSING || importState === ImportState.IMPORTING) && renderProgress()}
        {importState === ImportState.COMPLETED && renderResults()}

        {error && <div className="error-message">{error}</div>}
      </div>

      <div className="buttonDiv">
        <button
          className="cancelButton"
          onClick={handleCancel}
          disabled={importState === ImportState.PARSING}
        >
          {importState === ImportState.IMPORTING ? "Minimize" :
           importState === ImportState.DATA_REVIEW ? "Back" : "Cancel"}
        </button>

        {importState === ImportState.FILE_SELECTION && (
          <button
            className="importButton"
            disabled={!selectedFile}
            onClick={handleImport}
          >
            Import
          </button>
        )}

        {importState === ImportState.DATA_REVIEW && (
          <button
            className="importButton"
            onClick={startImportProcess}
          >
           Import
          </button>
        )}

        {importState === ImportState.COMPLETED && (
          <button
            className="importButton"
            onClick={() => {
              setIsModalVisible?.(false);
              resetModal();
            }}
          >
            Close
          </button>
        )}
      </div>
    </CsvImportModalStyle>
  );
};

export default CsvImportModal;
